// Popover (DESIGN §3.7.3): Radix Popover. Fläche `overlay`, `shadow-2`, Radius 12 (breit ab 320: 16),
// Innenabstand 12–16, Abstand 8 zum Auslöser, Pfeil nur bei Info-Popovern, Kollisionsvermeidung,
// max. Höhe = Viewport − 32. Hinein 150 ms Deckkraft + 4 px Versatz.
import type { ComponentProps, ReactNode } from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { floatingSurface } from './_lib/menuStyles';
import './_lib/motion.css';
import { useLayerStyle } from './_lib/layer';
import { cn } from '../basis/kern';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;

export interface PopoverContentProps extends ComponentProps<typeof PopoverPrimitive.Content> {
  /** Pfeil zum Auslöser (nur Info-Popover). */
  arrow?: boolean;
  /** `sm` Innenabstand 12, `md` 16 und Radius 16 (breite Popover). */
  size?: 'sm' | 'md';
  children: ReactNode;
}

export function PopoverContent({
  arrow = false,
  size = 'sm',
  sideOffset = 8,
  collisionPadding = 16,
  className,
  children,
  style,
  ...rest
}: PopoverContentProps) {
  const layerStyle = useLayerStyle(style);
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...rest}
        style={layerStyle}
        className={cn(
          floatingSurface,
          'max-h-[calc(var(--radix-popover-content-available-height)-16px)] max-w-[calc(100vw-32px)] overflow-y-auto',
          'text-label',
          size === 'sm' ? 'p-3' : 'rounded-xl p-4',
          className,
        )}
      >
        {children}
        {arrow ? <PopoverPrimitive.Arrow width={16} height={8} className="fill-overlay" /> : null}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}
